import { Container, Row, Col, Card, Button, Spinner, Alert, ListGroup, Modal, Form, Badge, Toast, ToastContainer } from 'react-bootstrap';
import { Settings, Plus, RefreshCw, Edit, History, Copy, Code, Play, Trash2 } from 'lucide-react';
import { useRuleEngine } from '../../../apps/hooks/rule-engine/useRuleEngine';
import { useState, useEffect, useRef } from 'react';
import { DecisionGraph, JdmConfigProvider } from '@gorules/jdm-editor';
import { ConfigProvider } from 'antd';
import '@gorules/jdm-editor/dist/style.css';

const RuleEnginePage = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .ant-popover,
      .ant-dropdown,
      .ant-select-dropdown,
      .ant-picker-dropdown {
        z-index: 2000 !important;
      }
      .ant-popover .cm-editor {
        pointer-events: auto !important;
      }
      .ant-popover-inner-content {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const {
    listRuleFiles,
    isLoadingFiles,
    filesError,
    createOrUpdateRuleFile,
    isCreatingOrUpdatingFile,
    reloadRule,
    isReloading,
    rollbackRuleFile,
    isRollingBack,
    evaluateLocalRule,
    isEvaluatingLocal,
    evaluateLocalResult,
    evaluateLocalError,
    evaluateRule,
    isEvaluating,
    evaluateResult,
    deleteRuleFile,
    isDeleting,
  } = useRuleEngine();

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showSaveRuleModal, setShowSaveRuleModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showEvaluateModal, setShowEvaluateModal] = useState(false);
  const [showRemoteEvaluateModal, setShowRemoteEvaluateModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedRuleKey, setSelectedRuleKey] = useState<string>('');
  const [historyRuleKey, setHistoryRuleKey] = useState<string>('');
  const [remoteEvaluateRuleKey, setRemoteEvaluateRuleKey] = useState<string>('');
  const [deleteRuleKey, setDeleteRuleKey] = useState<string>('');
  const [isNewRule, setIsNewRule] = useState(false);
  const [newRuleKey, setNewRuleKey] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [editedContent, setEditedContent] = useState<any>(null);
  const [jsonContent, setJsonContent] = useState('');
  const [evaluateInput, setEvaluateInput] = useState(
    JSON.stringify(
      {
        productId: '123',
        userId: 'user-456',
        quantity: 2,
        price: 100,
      },
      null,
      2
    )
  );
  const [remoteEvaluateInput, setRemoteEvaluateInput] = useState(
    JSON.stringify(
      {
        productId: '123',
        userId: 'user-456',
        quantity: 2,
        price: 100,
      },
      null,
      2
    )
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

  const ruleFiles = listRuleFiles?.ruleFiles || [];
  const getRuleFileResult = useRuleEngine().getRuleFile(selectedRuleKey);
  const currentRuleFile = getRuleFileResult.data;
  const ruleFileContent = currentRuleFile?.content
    ? typeof currentRuleFile.content === 'string'
      ? JSON.parse(currentRuleFile.content)
      : currentRuleFile.content
    : null;

  const listVersionsResult = useRuleEngine().listRuleFileVersions(historyRuleKey);
  const versions = (listVersionsResult.data?.ruleFiles || []).sort((a, b) => b.version - a.version);

  useEffect(() => {
    if (ruleFileContent && !isNewRule && !editedContent) {
      setEditedContent(ruleFileContent);
    }
  }, [ruleFileContent, isNewRule]);

  useEffect(() => {
    if (evaluateLocalError) {
      showToastMessage(
        evaluateLocalError instanceof Error ? `Evaluation error: ${evaluateLocalError.message}` : 'Evaluation failed!',
        'danger'
      );
    }
  }, [evaluateLocalError]);

  const showToastMessage = (message: string, variant: 'success' | 'danger' = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const handleReloadRule = (ruleKey: string) => {
    reloadRule(ruleKey);
  };

  const handleOpenDeleteConfirm = (ruleKey: string) => {
    setDeleteRuleKey(ruleKey);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteRule = () => {
    if (!deleteRuleKey) return;

    deleteRuleFile(deleteRuleKey, {
      onSuccess: () => {
        setShowDeleteConfirmModal(false);
        setDeleteRuleKey('');
        showToastMessage('Rule deleted successfully!');
      },
      onError: (error) => {
        showToastMessage(
          error instanceof Error ? `Delete failed: ${error.message}` : 'Failed to delete rule!',
          'danger'
        );
      },
    });
  };

  const handleOpenHistory = (ruleKey: string) => {
    setHistoryRuleKey(ruleKey);
    setShowHistoryModal(true);
  };

  const handleRollback = (version: number) => {
    if (!historyRuleKey) return;

    rollbackRuleFile(
      {
        ruleKey: historyRuleKey,
        request: { targetVersion: version },
      },
      {
        onSuccess: () => {
          setShowHistoryModal(false);
          handleReloadRule(historyRuleKey);
        },
      },
    );
  };

  const handleViewJson = () => {
    if (!editedContent) return;
    setJsonContent(JSON.stringify(editedContent, null, 2));
    setShowJsonModal(true);
  };

  const handleCopyJsonFromModal = () => {
    navigator.clipboard.writeText(jsonContent);
    showToastMessage('JSON copied to clipboard!');
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonContent);
      setEditedContent(parsed);
      setShowJsonModal(false);
      showToastMessage('JSON applied successfully!');
    } catch (error) {
      showToastMessage('Invalid JSON format!', 'danger');
    }
  };

  const handleOpenEvaluate = () => {
    setShowEvaluateModal(true);
  };

  const handleTestEvaluate = () => {
    if (!editedContent) {
      showToastMessage('No rule content to evaluate!', 'danger');
      return;
    }

    let inputData;
    try {
      inputData = JSON.parse(evaluateInput);
    } catch (parseError) {
      showToastMessage('Invalid JSON format in input data!', 'danger');
      return;
    }

    const contentString = typeof editedContent === 'string' ? editedContent : JSON.stringify(editedContent);

    evaluateLocalRule({
      content: contentString,
      input: inputData,
    });
  };

  const handleOpenRemoteEvaluate = (ruleKey: string) => {
    setRemoteEvaluateRuleKey(ruleKey);
    setShowRemoteEvaluateModal(true);
  };

  const handleRemoteEvaluate = () => {
    if (!remoteEvaluateRuleKey) {
      showToastMessage('No rule key selected!', 'danger');
      return;
    }

    let inputData;
    try {
      inputData = JSON.parse(remoteEvaluateInput);
    } catch (parseError) {
      showToastMessage('Invalid JSON format in input data!', 'danger');
      return;
    }

    evaluateRule({
      ruleKey: remoteEvaluateRuleKey,
      request: {
        input: inputData,
      },
    });
  };

  const handleOpenEditor = (ruleKey: string) => {
    const file = ruleFiles.find((f) => f.ruleKey === ruleKey);
    setSelectedRuleKey(ruleKey);
    setRuleName(file?.name || '');
    setRuleDescription(file?.description || '');
    setEditedContent(null);
    setIsNewRule(false);
    setShowEditorModal(true);
  };

  const handleCreateNewRule = () => {
    const defaultContent = {
      contentType: 'application/vnd.gorules.decision',
      nodes: [],
      edges: [],
    };

    setSelectedRuleKey('');
    setNewRuleKey('');
    setRuleName('');
    setRuleDescription('');
    setEditedContent(defaultContent);
    setIsNewRule(true);
    setShowEditorModal(true);
  };

  const handleSaveButtonClick = () => {
    if (!editedContent) return;
    
    if (isNewRule) {
      setShowSaveRuleModal(true);
    } else {
      handleSaveRuleFile();
    }
  };

  const handleSaveRuleFile = () => {
    if (!selectedRuleKey || !editedContent || !ruleName || !ruleDescription) return;

    createOrUpdateRuleFile(
      {
        ruleKey: selectedRuleKey,
        request: {
          name: ruleName,
          description: ruleDescription,
          content: editedContent,
        },
      },
      {
        onSuccess: () => {
          setShowEditorModal(false);
          setShowSaveRuleModal(false);
          setNewRuleKey('');
          setRuleName('');
          setRuleDescription('');
          setIsNewRule(false);
          handleReloadRule(selectedRuleKey);
        },
      },
    );
  };

  const handleSaveNewRule = () => {
    if (!newRuleKey || !editedContent || !ruleName || !ruleDescription) return;

    createOrUpdateRuleFile(
      {
        ruleKey: newRuleKey,
        request: {
          name: ruleName,
          description: ruleDescription,
          content: editedContent,
        },
      },
      {
        onSuccess: () => {
          setShowSaveRuleModal(false);
          setSelectedRuleKey(newRuleKey);
          setIsNewRule(false);
          setNewRuleKey('');
          handleReloadRule(newRuleKey);
        },
      },
    );
  };

  return (
    <Container className="mt-4">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="mb-2">
                <Settings className="me-2" size={32} />
                Rule Engine Management
              </h1>
              <p className="text-muted">Manage business rules with ZEN Engine and JDM Editor</p>
            </div>
            <Button variant="primary" onClick={handleCreateNewRule}>
              <Plus className="me-2" size={16} />
              New Rule
            </Button>
          </div>
        </Col>
      </Row>

      {filesError && (
        <Alert variant="danger" className="mb-4">
          <strong>Error loading rules:</strong> {filesError instanceof Error ? filesError.message : 'Unknown error'}
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Rule Files</h5>
            </Card.Header>
            <Card.Body>
              {isLoadingFiles ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading rule files...</span>
                  </Spinner>
                </div>
              ) : !ruleFiles.length ? (
                <Alert variant="info">No rule files yet. Create your first rule!</Alert>
              ) : (
                <ListGroup>
                  {ruleFiles.map((file) => (
                    <ListGroup.Item key={file.ruleKey} className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">{file.name}</h6>
                        <div className="text-muted small">
                          <code>{file.ruleKey}</code> • Version {file.version}
                        </div>
                        {file.description && <div className="text-muted small mt-1">{file.description}</div>}
                        <div className="text-muted small">Updated {new Date(file.updatedAt).toLocaleString()}</div>
                      </div>
                      <div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleOpenEditor(file.ruleKey)}
                        >
                          <Edit size={14} className="me-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleReloadRule(file.ruleKey)}
                          disabled={isReloading}
                        >
                          <RefreshCw size={14} className="me-1" />
                          Reload
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2"
                          onClick={() => handleOpenRemoteEvaluate(file.ruleKey)}
                        >
                          <Play size={14} className="me-1" />
                          Evaluate
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleOpenHistory(file.ruleKey)}
                        >
                          <History size={14} className="me-1" />
                          History
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleOpenDeleteConfirm(file.ruleKey)}
                          disabled={isDeleting}
                        >
                          <Trash2 size={14} className="me-1" />
                          Delete
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showSaveRuleModal}
        onHide={() => {
          setShowSaveRuleModal(false);
          setNewRuleKey('');
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Save New Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Rule Key *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., upsell-main, shipping-calculator"
                value={newRuleKey}
                onChange={(e) => setNewRuleKey(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Unique identifier for the rule file (lowercase, hyphens allowed)
              </Form.Text>
            </Form.Group>
            <Alert variant="info" className="mb-0">
              <small>Name: <strong>{ruleName || '(not set)'}</strong></small>
              <br />
              <small>Description: <strong>{ruleDescription || '(not set)'}</strong></small>
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSaveRuleModal(false);
              setNewRuleKey('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveNewRule}
            disabled={isCreatingOrUpdatingFile || !newRuleKey || !ruleName || !ruleDescription}
          >
            {isCreatingOrUpdatingFile ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Create Rule'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal 
        show={showEditorModal} 
        onHide={() => setShowEditorModal(false)} 
        size="xl" 
        fullscreen
        style={{ zIndex: 1050 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Rule Editor {isNewRule ? '- New Rule' : `- ${ruleName || selectedRuleKey}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body ref={modalBodyRef} style={{ padding: 0, height: 'calc(100vh - 120px)' }}>
          <div style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={ruleName}
                    onChange={(e) => setRuleName(e.target.value)}
                    placeholder="e.g., Upsell Product Rules"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Description</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={ruleDescription}
                    onChange={(e) => setRuleDescription(e.target.value)}
                    placeholder="Describe the purpose..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
          <div style={{ width: '100%', height: 'calc(100% - 80px)', position: 'relative' }}>
            {editedContent ? (
              <ConfigProvider
                getPopupContainer={() => modalBodyRef.current || document.body}
              >
                <JdmConfigProvider>
                  <DecisionGraph
                    value={editedContent}
                    onChange={(val) => {
                      setEditedContent(val);
                    }}
                  />
                </JdmConfigProvider>
              </ConfigProvider>
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading rule file...</span>
                </Spinner>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={handleViewJson} disabled={!editedContent}>
                <Code size={14} className="me-1" />
                View JSON
              </Button>
              <Button variant="outline-info" size="sm" onClick={handleOpenEvaluate} disabled={!editedContent}>
                <Play size={14} className="me-1" />
                Test Evaluate
              </Button>
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditorModal(false);
                  setIsNewRule(false);
                  setNewRuleKey('');
                  setRuleName('');
                  setRuleDescription('');
                  setEditedContent(null);
                }}
                disabled={isCreatingOrUpdatingFile}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="ms-2"
                onClick={handleSaveButtonClick}
                disabled={
                  isCreatingOrUpdatingFile ||
                  !editedContent ||
                  !ruleName ||
                  !ruleDescription ||
                  (!isNewRule && !selectedRuleKey)
                }
              >
                {isCreatingOrUpdatingFile ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : isNewRule ? (
                  'Save New Rule'
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Version History - {historyRuleKey}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {listVersionsResult.isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading versions...</span>
              </Spinner>
            </div>
          ) : !versions.length ? (
            <Alert variant="info">No version history available.</Alert>
          ) : (
            <ListGroup>
              {versions.map((version, index) => (
                <ListGroup.Item key={version.version}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 className="mb-0">Version {version.version}</h6>
                        {index === 0 && (
                          <Badge bg="success" className="ms-2">
                            Current
                          </Badge>
                        )}
                      </div>
                      <div className="text-muted small">
                        <strong>{version.name}</strong>
                      </div>
                      {version.description && (
                        <div className="text-muted small">{version.description}</div>
                      )}
                      <div className="text-muted small mt-1">
                        Created: {new Date(version.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {index !== 0 && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleRollback(version.version)}
                        disabled={isRollingBack}
                      >
                        {isRollingBack ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Rolling back...
                          </>
                        ) : (
                          'Rollback'
                        )}
                      </Button>
                    )}
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showJsonModal} onHide={() => setShowJsonModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>View / Edit Rule Content (JSON)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Rule Content JSON</Form.Label>
              <Form.Control
                as="textarea"
                rows={15}
                value={jsonContent}
                onChange={(e) => setJsonContent(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              <Form.Text className="text-muted">
                View or edit the JSON content directly. Make sure it's valid JSON before applying.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-secondary" onClick={handleCopyJsonFromModal}>
              <Copy size={14} className="me-1" />
              Copy to Clipboard
            </Button>
            <div>
              <Button variant="secondary" onClick={() => setShowJsonModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApplyJson}>
                Apply Changes
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showEvaluateModal} onHide={() => setShowEvaluateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Test Evaluate Rule (Server-side ZEN Engine)
            {selectedRuleKey && ` - ${selectedRuleKey}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Input Data (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={evaluateInput}
                onChange={(e) => setEvaluateInput(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                placeholder='{"key": "value"}'
              />
              <Form.Text className="text-muted">Enter the input data as JSON object</Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" onClick={handleTestEvaluate} disabled={isEvaluatingLocal}>
                {isEvaluatingLocal ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Evaluating...
                  </>
                ) : (
                  <>
                    <Play size={14} className="me-1" />
                    Run Evaluation
                  </>
                )}
              </Button>
            </div>

            {evaluateLocalResult && (
              <div>
                <Alert variant="success">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Evaluation Result</strong>
                    <Badge bg="info">Execution time: {evaluateLocalResult.executionTime}ms</Badge>
                  </div>
                  <Form.Label className="mb-2">Output:</Form.Label>
                  <pre
                    style={{
                      marginBottom: 0,
                      maxHeight: '200px',
                      overflow: 'auto',
                      fontSize: '12px',
                      backgroundColor: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                    }}
                  >
                    {JSON.stringify(evaluateLocalResult.result, null, 2)}
                  </pre>
                  {evaluateLocalResult.trace && (
                    <>
                      <Form.Label className="mt-3 mb-2">Trace:</Form.Label>
                      <pre
                        style={{
                          marginBottom: 0,
                          maxHeight: '200px',
                          overflow: 'auto',
                          fontSize: '11px',
                          backgroundColor: '#f8f9fa',
                          padding: '10px',
                          borderRadius: '4px',
                        }}
                      >
                        {JSON.stringify(evaluateLocalResult.trace, null, 2)}
                      </pre>
                    </>
                  )}
                </Alert>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEvaluateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRemoteEvaluateModal} onHide={() => setShowRemoteEvaluateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Evaluate Rule (Remote - Saved Version)
            {remoteEvaluateRuleKey && ` - ${remoteEvaluateRuleKey}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Input Data (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={remoteEvaluateInput}
                onChange={(e) => setRemoteEvaluateInput(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                placeholder='{"key": "value"}'
              />
              <Form.Text className="text-muted">
                Enter the input data as JSON object. This will evaluate the currently saved version of the rule on the
                server.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" onClick={handleRemoteEvaluate} disabled={isEvaluating}>
                {isEvaluating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Evaluating...
                  </>
                ) : (
                  <>
                    <Play size={14} className="me-1" />
                    Run Evaluation
                  </>
                )}
              </Button>
            </div>

            {evaluateResult && (
              <div>
                <Alert variant="success">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Evaluation Result</strong>
                    <Badge bg="info">Execution time: {evaluateResult.executionTime}ms</Badge>
                  </div>
                  <Form.Label className="mb-2">Output:</Form.Label>
                  <pre
                    style={{
                      marginBottom: 0,
                      maxHeight: '200px',
                      overflow: 'auto',
                      fontSize: '12px',
                      backgroundColor: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                    }}
                  >
                    {JSON.stringify(evaluateResult.result, null, 2)}
                  </pre>
                </Alert>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoteEvaluateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirmModal}
        onHide={() => {
          setShowDeleteConfirmModal(false);
          setDeleteRuleKey('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning!</strong> This action cannot be undone.
          </Alert>
          <p>
            Are you sure you want to delete the rule: <strong><code>{deleteRuleKey}</code></strong>?
          </p>
          <p className="text-muted mb-0">
            All versions of this rule will be permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteConfirmModal(false);
              setDeleteRuleKey('');
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteRule}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={14} className="me-1" />
                Delete Rule
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RuleEnginePage;
